import { FormInput, type FormInputProps } from "@prismicio/editor-ui"
import {
  type Control,
  type FieldValues,
  type Path,
  useController,
} from "react-hook-form"

interface HookFormInputProps<T extends FieldValues>
  extends Omit<
    FormInputProps,
    "value" | "onValueChange" | "onBlur" | "ref" | "error"
  > {
  /**
   * The control object provided by `react-hook-form`'s `useForm` or `useFormContext`.
   * This allows the component to interact with the form state.
   * It is provided from the outside to make `name` prop type-safe.
   */
  control: Control<T>
  /**
   * The unique name of the field within the form schema.
   * This prop defines which field of the form data this component will manage.
   */
  name: Path<T>
}

/**
 * A component used to bind a FormInput to a react-hook-form field.
 */
export function HookFormInput<T extends FieldValues>(
  props: HookFormInputProps<T>,
) {
  const { control, name, ...formInputProps } = props

  const { field, fieldState } = useController({
    control,
    name,
  })

  return (
    <FormInput
      {...formInputProps}
      value={field.value}
      onValueChange={field.onChange}
      onBlur={field.onBlur}
      ref={field.ref}
      error={fieldState.error?.message}
    />
  )
}
